import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  alpha,
  styled,
  useMediaQuery,
  useTheme
} from "@mui/material";

import { useContext, useEffect } from "react";

import Logo from "@/components/LogoSign";
import Scrollbar from "@/components/Scrollbar";
import { SidebarContext, widthMenuHide } from "@/contexts/SidebarContext";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { SidebarFooter } from "./SidebarFooter";
import SidebarMenu from "./SidebarMenu";

const CloseTwoToneIcon = dynamic(() => import("@mui/icons-material/CloseTwoTone"), { ssr: false });

function SetupLockedNotice() {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack spacing={1.5} sx={{ px: 2.5, py: 3 }}>
      <Chip label={t("Setup locked")} color="warning" variant="outlined" sx={{ alignSelf: "flex-start" }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.colors.alpha.trueWhite[100] }}>
        {t("Finish workspace personalization to unlock navigation.")}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.colors.alpha.trueWhite[70] }}>
        {t("The sidebar stays disabled until the required setup step on the signup page is complete.")}
      </Typography>
    </Stack>
  );
}

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        // width: ${theme.sidebar.width};
        // min-width: ${theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        position: relative;
        z-index: 7;
        height: 100%;
`
);

function Sidebar({ setupLocked = false }: { setupLocked?: boolean }) {
  const { t } = useTranslation();
  const { sidebarToggle, closeSidebar, menuState } = useContext(SidebarContext);
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const router = useRouter();

  useEffect(() => {
    closeSidebar();
  }, [router.asPath]);

  return (
    <>
      <SidebarWrapper
        sx={{
          display: {
            xs: "none",
            lg: "flex"
          },
          flexDirection: "column",
          justifyContent: "center",
          position: "fixed",
          // left: menuState ? 0 : `calc(-${theme.sidebar.width} + ${widthMenuHide})`,
          width: `${menuState ? theme.sidebar.width : widthMenuHide}`,
          minWidth: { widthMenuHide },
          top: 0,
          background:
            theme.palette.mode === "dark" ? alpha(theme.sidebar.background, 1) : alpha(theme.sidebar.background, 1),
          boxShadow: theme.palette.mode === "dark" ? theme.sidebar.boxShadow : theme.sidebar.boxShadow
        }}
      >
        <Box sx={{ mt: "22px" }}>
          <Logo />
        </Box>
        <Divider
          sx={{
            mt: theme.spacing(2),
            mx: theme.spacing(2),
            background: theme.palette.mode === "dark" ? theme.colors.alpha.trueWhite[100] : theme.colors.alpha.black[30]
          }}
        />
        <Scrollbar>{setupLocked ? <SetupLockedNotice /> : <SidebarMenu />}</Scrollbar>

        {setupLocked ? null : <SidebarFooter />}
      </SidebarWrapper>

      {/* sidebar for smaller size */}
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
          // Only show the temporary/mobile drawer on small screens.
          display: { xs: "block", lg: "none" }
        }}
        anchor={theme.direction === "rtl" ? "right" : "left"}
        open={sidebarToggle && !isLgUp}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            width: { xs: "100vw", sm: theme.sidebar.width },
            background:
              theme.palette.mode === "dark" ? alpha(theme.sidebar.background, 1) : theme.palette.background.default,
            boxShadow: `${theme.sidebar.boxShadow}`,
            overflowY: "auto",

            "::-webkit-scrollbar": {
              display: "none",
              width: "1px",
              backgroundColor: "transparent"
            },
            "::-webkit-scrollbar-thumb": {
              background: "transparent"
            }
          }}
        >
          <Box
            py={1}
            sx={{
              display: { xs: "flex", sm: "block" },
              justifyContent: { xs: "space-between", sm: "start" },
              alignItems: "center",
              background:
                theme.palette.mode === "dark" ? alpha(theme.sidebar.background, 1) : alpha(theme.sidebar.background, 1)
            }}
          >
            <Logo />
            <Box sx={{ display: { xs: "flex", sm: "none" } }}>
              <IconButton color="primary" onClick={closeSidebar} aria-label={t("Close sidebar")}>
                <CloseTwoToneIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Divider
            sx={{
              mt: theme.spacing(2),
              mx: theme.spacing(2),
              background: theme.colors.alpha.trueWhite[10]
            }}
          />
          {/* <Scrollbar> */}
          <>
            {setupLocked ? (
              <SetupLockedNotice />
            ) : (
              <>
                <SidebarMenu />
                <Divider
                  sx={{
                    background: theme.colors.alpha.trueWhite[10],
                    mx: theme.spacing(2)
                  }}
                />
                <SidebarFooter />
              </>
            )}
          </>
          {/* </Scrollbar> */}
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
